/**
 * AppleCalendarSettings — connect iCloud Calendar via CalDAV (#354).
 *
 * Different flow from Google/Microsoft because Apple doesn't do OAuth.
 * The user has to generate an app-specific password at appleid.apple.com
 * and paste it here alongside their Apple ID. The connect mutation
 * ships those creds to apple-calendar-sync which verifies against
 * iCloud's CalDAV endpoint and stores an encrypted connection.
 *
 * UI is a two-step form so the app-specific password instructions
 * are unmissable — the #1 support ticket is "I used my regular Apple
 * ID password" which iCloud silently rejects.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Calendar, CheckCircle2, RefreshCw, Unplug, ExternalLink, KeyRound } from "lucide-react";
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { NovaSpinner } from "@/components/nova";
import { supabase } from "@/integrations/supabase/client";
import { useOrgId } from "@/hooks/useOrgId";
import { format } from "date-fns";

interface AppleCalConn {
  id: string;
  apple_id: string | null;
  calendar_display_name: string | null;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

function useAppleConnection() {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ["apple_cal_connection", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("apple_calendar_connections" as any)
        .select("id, apple_id, calendar_display_name, last_sync_at, last_sync_error")
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return (data as unknown as AppleCalConn) ?? null;
    },
  });
}

export default function AppleCalendarSettings() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: conn, isLoading } = useAppleConnection();
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [appleId, setAppleId] = useState("");
  const [password, setPassword] = useState("");

  const connect = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("apple-calendar-sync", {
        body: { op: "connect", apple_id: appleId, password },
      });
      if (error) throw error;
      return data as { ok: boolean; apple_id: string; calendar: string };
    },
    onSuccess: (d) => {
      toast({ title: "Connected", description: `Linked ${d.apple_id} — syncing "${d.calendar}".` });
      setShowConnectForm(false);
      setAppleId("");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["apple_cal_connection"] });
    },
    onError: (e: any) => toast({ title: "Connection failed", description: e.message, variant: "destructive" }),
  });

  const syncNow = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("apple-calendar-sync", { body: { op: "pull" } });
      if (error) throw error;
      return data;
    },
    onSuccess: (d: any) => {
      toast({ title: "Synced", description: `Pulled ${d?.pulled ?? 0} events from iCloud.` });
      qc.invalidateQueries({ queryKey: ["apple_cal_connection"] });
    },
    onError: (e: any) => toast({ title: "Sync failed", description: e.message, variant: "destructive" }),
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke("apple-calendar-sync", { body: { op: "disconnect" } });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apple_cal_connection"] }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><NovaSpinner size={24} /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <OrbitIcon name="calendar" fallback={Calendar} className="w-5 h-5" /> Apple Calendar (iCloud)
          {conn && <Badge variant="default" className="gap-1"><OrbitIcon name="checkmark.circle.fill" fallback={CheckCircle2} className="w-3 h-3" /> Connected</Badge>}
        </CardTitle>
        <CardDescription>Two-way sync with iCloud Calendar via an app-specific password.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {conn ? (
          <>
            <div className="text-sm">
              <p>Connected as <span className="font-medium">{conn.apple_id}</span></p>
              {conn.calendar_display_name && (
                <p className="text-muted-foreground mt-1">
                  Syncing "<span className="text-foreground">{conn.calendar_display_name}</span>"
                </p>
              )}
              <p className="text-muted-foreground mt-1">
                {conn.last_sync_at
                  ? `Last synced ${format(new Date(conn.last_sync_at), "MMM d, h:mm a")}`
                  : "Not synced yet"}
              </p>
              {conn.last_sync_error && (
                <p className="text-destructive mt-1 text-xs">{conn.last_sync_error}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => syncNow.mutate()} disabled={syncNow.isPending}>
                {syncNow.isPending ? <NovaSpinner size={16} className="mr-2" /> : <OrbitIcon name="arrow.clockwise" fallback={RefreshCw} className="w-4 h-4 mr-2" />}
                Sync now
              </Button>
              <Button onClick={() => disconnect.mutate()} variant="outline" disabled={disconnect.isPending}>
                <OrbitIcon name="powerplug" fallback={Unplug} className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          </>
        ) : !showConnectForm ? (
          <Button onClick={() => setShowConnectForm(true)}>Connect Apple Calendar</Button>
        ) : (
          <div className="space-y-4">
            <Alert>
              <OrbitIcon name="key.fill" fallback={KeyRound} className="w-4 h-4" />
              <AlertDescription className="space-y-2">
                <p><strong>Use an app-specific password — not your regular Apple ID password.</strong></p>
                <p className="text-sm">
                  Apple requires an app-specific password for third-party calendar clients. Takes 30 seconds:
                </p>
                <ol className="text-sm list-decimal list-inside space-y-1 mt-2">
                  <li>Sign in at <a href="https://appleid.apple.com" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">appleid.apple.com <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-3 h-3" /></a></li>
                  <li>Under <em>Sign-In and Security</em>, tap <em>App-Specific Passwords</em></li>
                  <li>Generate one labeled "Orbit" and paste it below</li>
                </ol>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="apple-id">Apple ID</Label>
              <Input
                id="apple-id"
                type="email"
                autoComplete="username"
                placeholder="you@icloud.com"
                value={appleId}
                onChange={(e) => setAppleId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apple-password">App-specific password</Label>
              <Input
                id="apple-password"
                type="password"
                autoComplete="off"
                placeholder="xxxx-xxxx-xxxx-xxxx"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Stored encrypted. Only used to sync events to and from iCloud.</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => connect.mutate()}
                disabled={!appleId || !password || connect.isPending}
              >
                {connect.isPending && <NovaSpinner size={16} className="mr-2" />}
                Connect
              </Button>
              <Button variant="outline" onClick={() => { setShowConnectForm(false); setAppleId(""); setPassword(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
